import { apiRequest } from '@/serves/http'
import ApiPath from '@/serves/path'
import { getAppVersion, PLATFORM } from './credentials'
import { limitJsonBytes, uuid } from './utils'

/** 后端对 raw_result 的上限 */
const RAW_RESULT_MAX_BYTES = 300 * 1024

const RETRY_DELAY_MS = 1500

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/** 只有明确的网络层失败才值得重试；业务校验失败重试也没用 */
function isNetworkFailure(e) {
  if (!e) return false
  if (typeof e === 'string') return /timeout|network/i.test(e)
  if (e.request && !e.response) return true
  return /timeout|network/i.test(e.message || '')
}

/**
 * 上报一次测评结果。**永不 reject**：上报失败不能影响答题流程，
 * 后端也明确它不参与考试评分。
 *
 * @param {string} [clientRecordId] 幂等键，缺省新生成；与录音上传共用同一个值便于对账
 * @param {string} vendor 实际完成测评的厂商，取凭证响应里的 vendor
 * @param {string} [issueId] 凭证 credential.issue_id，报障对账用
 * @param {'exam'|'training'} [bizSource]
 * @param {string|number} bizId 业务 ID（水平测试：`{paper_id}_{题目 index}`）
 * @param {object} result 归一化 EvalResult
 * @returns {Promise<object|null>} 形如 {duplicated}，失败为 null
 */
export async function reportEvaluation({
  clientRecordId,
  vendor,
  issueId,
  bizSource = 'exam',
  bizId,
  questionId,
  attempt,
  refText,
  audioUrl,
  durationMs,
  result,
  evaluatedAt,
}) {
  if (!result || typeof result.totalScore !== 'number' || !vendor || !bizId || !refText) {
    console.warn('[speech] 上报参数不完整，跳过本次上报', { vendor, bizId, hasResult: !!result })
    return null
  }

  const body = {
    client_record_id: clientRecordId || uuid(),
    platform: PLATFORM,
    app_version: getAppVersion(),
    vendor,
    biz_source: bizSource,
    biz_id: String(bizId),
    ref_text: refText,
    total_score: result.totalScore,
    raw_result: limitJsonBytes(result.raw, RAW_RESULT_MAX_BYTES),
    evaluated_at: evaluatedAt || Date.now(),
  }
  if (issueId) body.issue_id = issueId
  if (questionId !== undefined && questionId !== null) body.question_id = String(questionId)
  if (typeof attempt === 'number') body.attempt = attempt
  if (audioUrl) body.audio_url = audioUrl
  if (typeof durationMs === 'number') body.duration_ms = Math.round(durationMs)
  // 厂商没返回的分项分**整个字段不传**（落库 NULL，与 0 分区分），不要拿 0 顶替
  if (typeof result.accuracyScore === 'number') body.accuracy_score = result.accuracyScore
  if (typeof result.fluencyScore === 'number') body.fluency_score = result.fluencyScore
  if (typeof result.completenessScore === 'number') {
    body.completeness_score = result.completenessScore
  }

  const send = async () => {
    const res = await apiRequest.post(ApiPath.speech_evaluations, body)
    return (res && !(res instanceof Error) && res.data) || {}
  }

  try {
    return await send()
  } catch (e) {
    if (!isNetworkFailure(e)) {
      console.warn('[speech] 测评结果上报失败', e)
      return null
    }
    // 网络失败原样重发一次：client_record_id 不变，服务端按幂等键去重
    await sleep(RETRY_DELAY_MS)
    try {
      return await send()
    } catch (e2) {
      console.warn('[speech] 测评结果上报重试仍失败', e2)
      return null
    }
  }
}
